import React, { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { MessageContext } from './MessageContext';
import TwoColumnSwitch from './utils/TwoColumnSwitch';
import { isAuthenticated } from './api/api';
import { ChevronsUp } from 'lucide-react';

const ChatMessage = ({ onSendMessage, disable }) => {
  const { selectedMessage } = useContext(MessageContext);
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const [textareaHeight, setTextareaHeight] = useState(48); // 初期高さ

  useEffect(() => {
    if (selectedMessage) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(selectedMessage, 'text/html');
      const text = doc.body.innerText;
      setMessage(text);
    }
  }, [selectedMessage]);

  // テキストエリアの高さを自動調整する関数
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 一時的に overflow を hidden にして高さをリセット
      textarea.style.overflow = 'hidden';
      textarea.style.height = 'auto';
  
      // 新しい高さを計算して設定
      const newHeight = Math.min(Math.max(textarea.scrollHeight - 18, 48), 200);
      textarea.style.height = `${newHeight}px`;
  
      // 元の overflow を戻す
      textarea.style.overflow = 'auto';
  
      // 状態を更新
      setTextareaHeight(newHeight);
    }
  };
  
  

  // メッセージの変更時に高さを調整
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (disable) return; 
    if (message.trim()) {
      onSendMessage(message, selectedCountry);
      setMessage('');
      // 送信後にテキストエリアの高さをリセット
      setTextareaHeight(48);
      if (textareaRef.current) {
        textareaRef.current.style.height = '48px';
      }
      if (containerRef.current) {
        containerRef.current.style.transform = 'translateY(0)';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) {
      return;
    }

    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      if (disable) return; 
      const textarea = e.target;
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = message.slice(0, cursorPosition);
      const textAfterCursor = message.slice(cursorPosition);

      // カーソル位置で改行を挿入
      setMessage(`${textBeforeCursor}\n${textAfterCursor}`);

      // 改行追加後に高さを調整
      setTimeout(adjustTextareaHeight, 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const countryOptions = [
    { id: 'JP', label: '日本' },
    { id: 'US', label: '米国' }
  ];

  const [selectedCountry, setSelectedCountry] = useState('JP');
  const onCountryChange = (value) => {
    setSelectedCountry(value);
  };

  return (
    <div className='absolute bottom-[50px] md:bottom-[50px] w-full px-2'>
      <div className="relative h-[100px]">
        <div 
          className="absolute bottom-0 w-full transition-transform duration-200 ease-in-out"
          ref={containerRef}
        >
          <div className="absolute top-[-30px] md:top-[-38px] w-full">
            <TwoColumnSwitch
              options={countryOptions}
              onChange={onCountryChange}
              defaultId={selectedCountry}
            />
          </div>
          <form id="message-form" onSubmit={handleSendMessage}>
            <div className="chat-input relative">
              <textarea
                ref={textareaRef}
                name="message"
                placeholder={`${isAuthenticated() ? 'ここに入力してください...（日米の上場企業が対象です）' : 'チャットをするにはログインが必要です'}`}
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleKeyDown}
                style={{ height: `${textareaHeight}px` }}
                disabled={!isAuthenticated()}
                className={`w-full min-h-[48px] max-h-[200px] resize-none overflow-y-auto rounded-xl px-4 py-3 pr-12 transition-all duration-200 ease-in-out border border-gray-200 ${!isAuthenticated() ? 'bg-gray-300' : 'bg-white'}`}
              />
               <button
                type="submit"
                className={`absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center 
                  ${disable || !isAuthenticated() ? 'cursor-not-allowed opacity-50 bg-gray-500' : 'bg-gray-700'}`}
                disabled={disable || !isAuthenticated()} // ボタン無効化
              >
                <ChevronsUp size={22} color="#ffffff" />
              </button>
            </div>
          </form>
        </div>
    </div>
    </div>
  );
};

ChatMessage.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
};

export default ChatMessage;

